<template>
  <div class="checklist-modal">
    <header class="checklist-header">
      <Title size="5">
        Load a Checklist template
      </Title>

      <IconButton
        class="checklist-close"
        icon="fas fa-times-circle"
        @click="emit('close')"
      />
    </header>

    <div class="checklist-scroller">
      <div class="checklist-scroller-body">
        <LoaderWithText v-if="isFetching" />

        <Stack
          v-else-if="availableChecklistsForEntity.length"
          gap="2"
        >
          <article
            v-for="checklist in availableChecklistsForEntity"
            :key="checklist.id"
            class="item"
            :class="{
              'is-selected': chosenId === checklist.id,
            }"
            @click="
              chosenId === checklist.id
                ? (chosenId = '')
                : (chosenId = checklist.id)
            "
          >
            <div class="item-header">
              <input
                type="radio"
                :checked="chosenId === checklist.id"
              >
              <Title
                class="item-header-name"
                size="4_5"
              >
                {{ checklist.name }}
              </Title>
              <Icon
                class="item-icon"
                icon="fas fa-check-circle"
              />
            </div>

            <p>
              {{ checklist.section_count }} sections —
              {{ checklist.item_count }} documents
            </p>
          </article>
        </Stack>

        <div v-else>
          No checklist found
        </div>
      </div>
    </div>

    <footer class="checklist-footer">
      <Button
        class="checklist-footer-button"
        :variant="hasAssigned ? 'success' : 'primary'"
        :icon-before="hasAssigned ? `fas fa-check` : `fas fa-tasks`"
        spacing="loose"
        :loading="isAssigning"
        :disabled="chosenId === ''"
        :static="hasAssigned"
        @click="selectChecklistTemplate"
      >
        {{ hasAssigned ? `Checklist Loaded` : `Load Checklist` }}
      </Button>
    </footer>
  </div>
</template>

<script setup>
import { defineEmits, onMounted, ref } from 'vue';
import { ToastProgrammatic as Toast } from 'buefy';
import {
  Button,
  Icon,
  IconButton,
  LoaderWithText,
  Stack,
  Title,
} from '@stakeui';

const props = defineProps({
  entityExternalId: {
    type: String,
    default: '',
  },
  hasChecklist: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'checklistLoaded']);

const csrfToken = ref('');
const chosenId = ref('');
const isFetching = ref(true);
const isAssigning = ref(false);
const hasAssigned = ref(false);
const availableChecklistsForEntity = ref([]);

const loadAvailableChecklists = async () => {
  if (availableChecklistsForEntity.value.length > 0) return;

  isFetching.value = true;

  try {
    const response = await fetch(
        `/api/v1/client-portal/checklists/available/${props.entityExternalId}/`,
    );

    if (!response.ok) {
      throw new Error(
          `Failed to fetch available checklists: ${response.status}`,
      );
    }

    const data = await response.json();
    availableChecklistsForEntity.value = data.checklists || [];
  } catch (err) {
    console.error('Error loading available checklists:', err);
    availableChecklistsForEntity.value = [];
  } finally {
    isFetching.value = false;
  }
};

const selectChecklistTemplate = async () => {
  if (props.hasChecklist && !window.confirm('This will replace your current checklist. Are you sure you want to proceed?')) {
    return;
  }

  isAssigning.value = true;

  try {
    const response = await fetch(
        `/api/v1/client-portal/checklists/${props.entityExternalId}/set-active/`,
        {
          method: 'POST',
          body: JSON.stringify({ checklist_id: chosenId.value }),
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken.value,
          },
        },
    );

    if (!response.ok) {
      const errData = await response.json().catch(() => ({}));
      throw new Error(
          `Failed to assign template: ${response.status} - ${
            errData.error || 'Server error'
          }`,
      );
    }

    hasAssigned.value = true;

    Toast.open({
      message: `Checklist successfully loaded.`,
      type: 'is-success',
    });

    emit('checklistLoaded');
  } catch (err) {
    console.error('Error selecting template:', err);
  } finally {
    isAssigning.value = false;
  }
};

onMounted(async () => {
  csrfToken.value = document.querySelector(
      '[name="csrfmiddlewaretoken"]',
  )?.value;
  await loadAvailableChecklists();
});
</script>

<style scoped>
.checklist-modal {
  --spacing: 1.5em;
  max-width: 100%;
  width: 32em;
}

.checklist-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing);
}

.checklist-close {
  color: var(--stk-text-weak);
  font-size: 1.25em;
  transition-duration: var(--stk-transition-duration);
  transition-property: color;

  &:hover {
    color: var(--stk-text-normal);
  }
}

.checklist-scroller {
  background-color: var(--stk-text-95);
  border-color: var(--stk-border);
  border-style: solid;
  border-width: 1px 0;
  box-shadow: inset 0 0.25em 0.5em rgb(0 0 0 / 5%);
  height: 20rem;
  overflow-y: scroll;
}

.checklist-scroller-body {
  padding: var(--spacing);
}

.item {
  background-color: #fff;
  border-radius: 0.75em;
  border: 1px solid var(--stk-border);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 0.25em;
  position: relative;
  padding: 1.125em 1.25em;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, color, border-color, box-shadow;

  &:hover {
    border-color: var(--stk-primary);
  }

  &.is-selected {
    border-color: var(--stk-primary);
    box-shadow: 0 0 0 1px var(--stk-primary),
      0 4px 4px 0 hsl(var(--stk-primary-hsl) / 20%);

    .item-icon {
      opacity: 1;
    }
  }
}

.item-header {
  display: flex;
  align-items: center;
  gap: 0.5em;
}

.item-header-name {
  flex-grow: 1;
}

.item-icon {
  color: var(--stk-primary);
  opacity: 0;
}

.checklist-footer {
  display: flex;
  justify-content: stretch;
  padding: 1.5em;
}

.checklist-footer-button {
  flex-grow: 1;
}
</style>
